import { tenantTypeLabel } from '@axioma/shared';
import { Badge, Button, KbdHint } from '@axioma/ui';
import { useMutation } from '@tanstack/react-query';
import {
  Bell,
  BellOff,
  Building2,
  ChevronsUpDown,
  Command,
  LogOut,
  Search,
  User as UserIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { usePushNotifications } from '../hooks/usePushNotifications.js';
import { api } from '../lib/api.js';
import { disconnectAllRealtime } from '../lib/realtime.js';
import { useAuthStore } from '../stores/auth.js';
import { useUIStore } from '../stores/ui.js';

export function TopBar(): JSX.Element {
  const { user, tenants, currentTenantId, setToken, setCurrentTenant, clear } = useAuthStore();
  const setCommandOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const [tenantOpen, setTenantOpen] = useState(false);
  const push = usePushNotifications();
  const navigate = useNavigate();

  const currentTenant = tenants.find((t) => t.id === currentTenantId);

  const switchMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<{ token: string }>('/auth/switch-tenant', { tenantId });
      return { token: data.token, tenantId };
    },
    onSuccess: ({ token, tenantId }) => {
      setToken(token);
      setCurrentTenant(tenantId);
      setTenantOpen(false);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
    onSettled: () => {
      disconnectAllRealtime();
      clear();
      window.location.href = '/login';
    },
  });

  return (
    <header className="flex h-[60px] items-center gap-3 border-b border-ax-line bg-ax-graphite px-5">
      {/* Tenant switcher */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setTenantOpen((v) => !v)}
          className="flex items-center gap-2 rounded-ax-md border border-ax-line bg-ax-night px-3 py-1.5 transition-colors hover:border-[color:rgb(58_61_70)]"
        >
          <Building2 size={14} className="text-ax-text-tertiary" />
          <div className="flex flex-col items-start leading-tight">
            <span className="ax-eyebrow text-[9px] text-ax-text-tertiary">Workspace</span>
            <span className="font-display text-sm tracking-tight text-ax-text-primary">
              {currentTenant?.name ?? 'Selecionar workspace'}
            </span>
          </div>
          <ChevronsUpDown size={14} className="ml-1 text-ax-text-tertiary" />
        </button>
        {tenantOpen && (
          <div className="absolute left-0 top-full z-ax-dropdown mt-2 w-80 overflow-hidden rounded-ax-md border border-ax-line bg-ax-graphite shadow-ax-lg">
            <div className="border-b border-ax-line px-3 py-2">
              <span className="ax-eyebrow text-ax-text-tertiary">Seus workspaces</span>
            </div>
            <ul className="max-h-72 overflow-y-auto">
              {tenants.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => switchMutation.mutate(t.id)}
                    className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-ax-night ${
                      t.id === currentTenantId ? 'bg-ax-night' : ''
                    }`}
                  >
                    <Building2 size={14} className="mt-0.5 text-ax-text-tertiary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-sm leading-tight text-ax-text-primary">
                        {t.name}
                      </p>
                      <p className="ax-body-sm truncate text-ax-text-tertiary">
                        {tenantTypeLabel(t.type)} · {t.role}
                      </p>
                    </div>
                    {t.id === currentTenantId && (
                      <Badge variant="aurora" uppercase>
                        atual
                      </Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Search / Command palette trigger */}
      <button
        type="button"
        onClick={() => setCommandOpen(true)}
        className="flex h-9 max-w-md flex-1 items-center gap-2 rounded-ax-md border border-ax-line bg-ax-night px-3 text-ax-text-tertiary transition-colors hover:border-[color:rgb(58_61_70)]"
      >
        <Search size={14} />
        <span className="flex-1 text-left text-[13px]">
          Buscar processos, peças, jurisprudência…
        </span>
        <KbdHint keys={['⌘', 'K']} />
      </button>

      <div className="flex-1" />

      {/* Push notifications toggle */}
      {push.status !== 'unsupported' && push.status !== 'unknown' && (
        <Button
          variant="ghost"
          size="sm"
          aria-label={
            push.status === 'subscribed' ? 'Desativar notificações' : 'Ativar notificações'
          }
          title={
            push.status === 'subscribed'
              ? 'Notificações ativas (clique para desativar)'
              : push.status === 'denied'
                ? 'Notificações bloqueadas no navegador'
                : 'Ativar notificações push'
          }
          loading={push.loading}
          disabled={push.status === 'denied'}
          onClick={() =>
            push.status === 'subscribed' ? void push.unsubscribe() : void push.subscribe()
          }
          leftIcon={
            push.status === 'subscribed' ? (
              <Bell size={14} className="text-ax-aurora" />
            ) : (
              <BellOff size={14} />
            )
          }
        />
      )}

      {/* User */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/perfil')}
          className="hidden rounded-ax-sm px-2 py-1 text-right transition-colors hover:bg-ax-night md:block"
          title="Abrir perfil profissional"
        >
          <p className="font-display text-sm leading-tight text-ax-text-primary">{user?.name}</p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-ax-text-tertiary">
            {user?.email}
          </p>
        </button>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Perfil"
          title="Perfil profissional"
          leftIcon={<UserIcon size={14} />}
          onClick={() => navigate('/perfil')}
        />
        <Button
          variant="ghost"
          size="sm"
          aria-label="Sair"
          leftIcon={<LogOut size={14} />}
          onClick={() => logoutMutation.mutate()}
        />
      </div>

      {/* Command palette ghost icon for screen-reader navigation hint */}
      <Command size={1} className="sr-only" aria-hidden="true" />
    </header>
  );
}
